import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import ArtistMembersTab from './ArtistMembersTab';
import { fetchAuthSession } from 'aws-amplify/auth';
import { toggleAttended } from '../attendedUtils';

const TABS = ['Show History', 'Members', 'Shows by Year', 'Festivals Played', 'Venues Played', 'Artist Relationships', 'Geographic Breakdown', 'Shows Map'];
const PAGE_SIZE = 25;

function Pagination({ currentPage, totalItems, onPageChange }) {
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) pages.push(i);

    return (
        <div style={styles.pagination}>
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}
            >
                ‹ Prev
            </button>
            {pages.map(p => (
                <button
                    key={p}
                    onClick={() => onPageChange(p)}
                    style={{ ...styles.pageBtn, ...(p === currentPage ? styles.pageBtnActive : {}) }}
                >
                    {p}
                </button>
            ))}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ ...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {}) }}
            >
                Next ›
            </button>
        </div>
    );
}

function ShowHistoryTab({ shows, attendedMap, onToggleAttended, togglingMap }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded yet.</div>;

    const q = search.toLowerCase();
    const filtered = search ? shows.filter(show => {
        const dateStr = show.event_date
            ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
            : '';
        return [dateStr, show.city, show.state, show.country, show.venue_name, show.festival_name, show.other_artists]
            .some(val => val && val.toLowerCase().includes(q));
    }) : shows;

    const totalShows = shows.length;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

    return (
        <div>
            <input
                type="text"
                placeholder="Search by date, city, state, country, venue, artists..."
                value={search}
                onChange={handleSearch}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No shows match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Show #</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>Additional Artists</th>
                                    <th style={styles.th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    const globalIdx = shows.indexOf(show);
                                    const showNum = totalShows - globalIdx;
                                    const dateObj = show.event_date ? new Date(show.event_date) : null;
                                    const formattedDate = dateObj
                                        ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                        : '—';
                                    const isAttended = attendedMap[show.event_id] === true;
                                    const isToggling = togglingMap[show.event_id];

                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                <Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{showNum}</Link>
                                            </td>
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formattedDate}</td>
                                            <td style={styles.td}>{show.city || '—'}</td>
                                            <td style={styles.td}>{show.state || '—'}</td>
                                            <td style={styles.td}>{show.country || '—'}</td>
                                            <td style={styles.td}>
                                                {show.venue_id
                                                    ? <Link to={`/venues/${show.venue_slug || show.venue_id}`} style={styles.tableLink}>{show.venue_name}</Link>
                                                    : show.venue_name || '—'}
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {show.festival_name && show.festival_slug
                                                    ? <><Link to={`/festivals/${show.festival_slug}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' }}>{show.festival_name}</Link>{show.other_artists ? ` - ${show.other_artists}` : ''}</>
                                                    : show.other_artists || '—'
                                                }
                                            </td>
                                            <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <button
                                                    onClick={() => onToggleAttended(show.event_id)}
                                                    disabled={isToggling}
                                                    style={isAttended ? styles.attendedBtnOn : styles.attendedBtnOff}
                                                >
                                                    {isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

function ShowsByYearTab({ shows }) {
    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded yet.</div>;

    // Build year buckets spanning first to last show year
    const years = shows.map(s => new Date(s.event_date).getUTCFullYear());
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);

    const counts = {};
    years.forEach(y => { counts[y] = (counts[y] || 0) + 1; });

    const data = [];
    for (let y = minYear; y <= maxYear; y++) {
        data.push({ year: y, count: counts[y] || 0 });
    }

    const maxCount = Math.max(...data.map(d => d.count), 1);
    const barMaxHeight = 200;

    // Y-axis grid lines
    const gridLines = [];
    const step = maxCount <= 5 ? 1 : maxCount <= 10 ? 2 : maxCount <= 20 ? 5 : 10;
    for (let v = 0; v <= maxCount; v += step) gridLines.push(v);
    if (gridLines[gridLines.length - 1] < maxCount) gridLines.push(maxCount);

    const chartWidth = Math.max(data.length * 52, 400);
    const yAxisWidth = 36;
    const chartPadTop = 16;
    const chartPadBottom = 40;
    const totalHeight = barMaxHeight + chartPadTop + chartPadBottom;

    return (
        <div>
            <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                <div style={{ display: 'flex', minWidth: chartWidth + yAxisWidth + 'px' }}>
                    {/* Y-axis */}
                    <div style={{ width: yAxisWidth + 'px', flexShrink: 0, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => {
                            const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight;
                            return (
                                <div key={v} style={{ position: 'absolute', top: top + 'px', right: '6px', transform: 'translateY(-50%)', fontSize: '11px', color: '#666', whiteSpace: 'nowrap' }}>
                                    {v}
                                </div>
                            );
                        })}
                    </div>
                    {/* Chart area */}
                    <div style={{ flex: 1, position: 'relative', height: totalHeight + 'px' }}>
                        {/* Grid lines */}
                        {gridLines.map(v => {
                            const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight;
                            return (
                                <div key={v} style={{ position: 'absolute', top: top + 'px', left: 0, right: 0, borderTop: `1px solid ${v === 0 ? '#444' : '#2a2a2a'}`, zIndex: 0 }} />
                            );
                        })}
                        {/* Bars */}
                        <div style={{ display: 'flex', alignItems: 'flex-end', height: barMaxHeight + 'px', marginTop: chartPadTop + 'px', gap: '4px', paddingLeft: '8px', paddingRight: '8px', position: 'relative', zIndex: 1 }}>
                            {data.map(({ year, count }) => {
                                const barH = count === 0 ? 0 : Math.max((count / maxCount) * barMaxHeight, 4);
                                return (
                                    <div key={year} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }} title={`${year}: ${count} show${count !== 1 ? 's' : ''}`}>
                                        {count > 0 && (
                                            <div style={{ fontSize: '11px', color: '#bb86fc', fontWeight: 'bold', opacity: 0.85 }}>{count}</div>
                                        )}
                                        <div style={{
                                            width: '100%',
                                            maxWidth: '44px',
                                            height: barH + 'px',
                                            backgroundColor: count === 0 ? 'transparent' : '#bb86fc',
                                            borderRadius: '3px 3px 0 0',
                                            transition: 'opacity 0.15s',
                                            cursor: count > 0 ? 'default' : 'default',
                                        }} />
                                    </div>
                                );
                            })}
                        </div>
                        {/* X-axis labels */}
                        <div style={{ display: 'flex', paddingLeft: '8px', paddingRight: '8px', gap: '4px', marginTop: '6px' }}>
                            {data.map(({ year, count }) => (
                                <div key={year} style={{ flex: 1, textAlign: 'center', fontSize: '11px', color: count === 0 ? '#444' : '#888', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                    {year}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '32px', fontSize: '13px', color: '#888' }}>
                <span>Total Years Active: <strong style={{ color: '#e0e0e0' }}>{data.filter(d => d.count > 0).length}</strong></span>
                <span>Peak Year: <strong style={{ color: '#bb86fc' }}>{data.reduce((best, d) => d.count > best.count ? d : best, data[0]).year} ({data.reduce((best, d) => d.count > best.count ? d : best, data[0]).count} shows)</strong></span>
            </div>
        </div>
    );
}

function GeographicMapTab({ showMap }) {
    const mapRef = React.useRef(null);
    const mapInstanceRef = React.useRef(null);
    const [leafletReady, setLeafletReady] = React.useState(!!window.L);

    React.useEffect(() => {
        if (window.L) { setLeafletReady(true); return; }
        // Load Leaflet CSS
        if (!document.getElementById('leaflet-css')) {
            const link = document.createElement('link');
            link.id = 'leaflet-css';
            link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);
        }
        // Load Leaflet JS
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => setLeafletReady(true);
        document.head.appendChild(script);
    }, []);

    React.useEffect(() => {
        if (!leafletReady || !mapRef.current || !showMap || showMap.length === 0) return;
        if (mapInstanceRef.current) {
            mapInstanceRef.current.remove();
            mapInstanceRef.current = null;
        }

        const L = window.L;
        const map = L.map(mapRef.current, { zoomControl: true });
        mapInstanceRef.current = map;

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors',
            maxZoom: 18,
        }).addTo(map);

        const markerIcon = L.divIcon({
            className: '',
            html: `<div style="width:12px;height:12px;background:#bb86fc;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.5);"></div>`,
            iconSize: [12, 12],
            iconAnchor: [6, 6],
            popupAnchor: [0, -8],
        });

        const bounds = [];
        showMap.forEach(venue => {
            const lat = parseFloat(venue.lat);
            const lng = parseFloat(venue.lng);
            if (isNaN(lat) || isNaN(lng)) return;

            bounds.push([lat, lng]);

            const dates = venue.show_dates
                ? venue.show_dates.split(',').map(d => {
                    const dt = new Date(d.trim());
                    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
                })
                : [];

            const locationStr = [venue.city, venue.state, venue.country].filter(Boolean).join(', ');
            const venueUrl = venue.venue_slug || venue.venue_id;
            const showCount = parseInt(venue.show_count);

            const popupHtml = `
                <div style="font-family:sans-serif;min-width:180px;max-width:260px;">
                    <div style="font-weight:bold;font-size:14px;margin-bottom:4px;">
                        <a href="/venues/${venueUrl}" style="color:#bb86fc;text-decoration:none;">${venue.venue_name} ↗</a>
                    </div>
                    ${locationStr ? `<div style="font-size:12px;color:#888;margin-bottom:6px;">${locationStr}</div>` : ''}
                    <div style="font-size:12px;color:#aaa;margin-bottom:${dates.length ? '6px' : '0'};">
                        <strong style="color:#e0e0e0;">${showCount}</strong> show${showCount !== 1 ? 's' : ''}
                    </div>
                    ${dates.length > 0 ? `
                        <div style="font-size:11px;color:#999;line-height:1.6;">
                            ${dates.slice(0, 10).join('<br/>')}
                            ${dates.length > 10 ? `<br/><em>+${dates.length - 10} more</em>` : ''}
                        </div>
                    ` : ''}
                </div>
            `;

            L.marker([lat, lng], { icon: markerIcon })
                .bindPopup(popupHtml, { maxWidth: 280 })
                .addTo(map);
        });

        if (bounds.length === 1) {
            map.setView(bounds[0], 12);
        } else if (bounds.length > 1) {
            map.fitBounds(bounds, { padding: [40, 40] });
        }

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [leafletReady, showMap]);

    if (!showMap || showMap.length === 0) {
        return <div style={styles.emptyTab}>No geocoded venues available yet.</div>;
    }

    return (
        <div>
            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#888' }}>
                {showMap.length} venue{showMap.length !== 1 ? 's' : ''} with location data · Click a pin for details
            </div>
            {!leafletReady ? (
                <div style={styles.emptyTab}>Loading map...</div>
            ) : (
                <div ref={mapRef} style={{ height: '480px', borderRadius: '8px', border: '1px solid #333', overflow: 'hidden' }} />
            )}
        </div>
    );
}

function FestivalsPlayedTab({ festivals, shows }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    // Festival shows: filter show_history to only festival shows
    const festivalShows = shows ? shows.filter(s => s.festival_id) : [];

    if (festivalShows.length === 0) return <div style={styles.emptyTab}>No festival shows recorded yet.</div>;

    const q = search.toLowerCase();
    const filtered = search ? festivalShows.filter(show => {
        const dateStr = show.event_date
            ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
            : '';
        return [dateStr, show.festival_name, show.city, show.state, show.country, show.venue_name, show.other_artists]
            .some(val => val && val.toLowerCase().includes(q));
    }) : festivalShows;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

    return (
        <div>
            <input
                type="text"
                placeholder="Search by date, festival, city, venue, artists..."
                value={search}
                onChange={handleSearch}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No festival shows match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Festival</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>Additional Artists</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    const dateObj = show.event_date ? new Date(show.event_date) : null;
                                    const formattedDate = dateObj
                                        ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
                                        : '—';
                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={styles.td}>
                                                <Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{formattedDate}</Link>
                                            </td>
                                            <td style={styles.td}>
                                                {show.festival_slug
                                                    ? <Link to={`/festivals/${show.festival_slug}`} style={styles.tableLink}>{show.festival_name}</Link>
                                                    : show.festival_name || '—'}
                                            </td>
                                            <td style={styles.td}>{show.city || '—'}</td>
                                            <td style={styles.td}>{show.state || '—'}</td>
                                            <td style={styles.td}>{show.country || '—'}</td>
                                            <td style={styles.td}>
                                                {show.venue_id
                                                    ? <Link to={`/venues/${show.venue_slug || show.venue_id}`} style={styles.tableLink}>{show.venue_name}</Link>
                                                    : show.venue_name || '—'}
                                            </td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {show.other_artists || '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

function VenuesPlayedTab({ venues }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!venues || venues.length === 0) return <div style={styles.emptyTab}>No venues recorded yet.</div>;

    const q = search.toLowerCase();
    const filtered = search ? venues.filter(v =>
        [v.name, v.city, v.state, v.country, v.capacity != null ? String(v.capacity) : '']
            .some(val => val && val.toLowerCase().includes(q))
    ) : venues;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

    return (
        <div>
            <input
                type="text"
                placeholder="Search by venue, city, state, country, capacity..."
                value={search}
                onChange={handleSearch}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No venues match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State/Region</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Capacity</th>
                                    <th style={styles.th}>Shows</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((venue, idx) => (
                                    <tr key={venue.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}>
                                            <Link to={`/venues/${venue.slug || venue.id}`} style={styles.tableLink}>{venue.name}</Link>
                                        </td>
                                        <td style={styles.td}>{venue.city || '—'}</td>
                                        <td style={styles.td}>{venue.state || '—'}</td>
                                        <td style={styles.td}>{venue.country || '—'}</td>
                                        <td style={styles.td}>{venue.capacity ? venue.capacity.toLocaleString() : '—'}</td>
                                        <td style={styles.td}>
                                            <span style={styles.showCount}>{venue.times_played}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

function ArtistRelationshipsTab({ playedWith }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    if (!playedWith || playedWith.length === 0) return <div style={styles.emptyTab}>No co-billed artists recorded yet.</div>;

    const q = search.toLowerCase();
    const filtered = search ? playedWith.filter(pw =>
        [pw.name, pw.city, pw.state, pw.country]
            .some(val => val && val.toLowerCase().includes(q))
    ) : playedWith;

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

    return (
        <div>
            <input
                type="text"
                placeholder="Search by artist, city, state, country..."
                value={search}
                onChange={handleSearch}
                style={styles.tabSearch}
            />
            {filtered.length === 0 ? (
                <div style={styles.emptyTab}>No artists match your search.</div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Artist</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State/Region</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Shows Together</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((pw, idx) => (
                                    <tr key={pw.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}>
                                            <Link to={`/artists/${pw.slug || pw.id}`} style={styles.tableLink}>{pw.name}</Link>
                                        </td>
                                        <td style={styles.td}>{pw.city || '—'}</td>
                                        <td style={styles.td}>{pw.state || '—'}</td>
                                        <td style={styles.td}>{pw.country || '—'}</td>
                                        <td style={styles.td}>
                                            <span style={styles.showCount}>{pw.times_played_together}x</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

function GeographicBreakdownTab({ geography }) {
    if (!geography || geography.length === 0) return <div style={styles.emptyTab}>No geographic data available.</div>;

    // Build hierarchical rollup: country > state > city > venue
    const rollup = {};
    geography.forEach(row => {
        const country = row.country || 'Unknown';
        const state = row.state || '—';
        const city = row.city || '—';
        const venueName = row.venue_name || '—';
        const venueId = row.venue_id;
        const count = parseInt(row.show_count) || 0;

        if (!rollup[country]) rollup[country] = { total: 0, states: {} };
        rollup[country].total += count;

        if (!rollup[country].states[state]) rollup[country].states[state] = { total: 0, cities: {} };
        rollup[country].states[state].total += count;

        if (!rollup[country].states[state].cities[city]) rollup[country].states[state].cities[city] = { total: 0, venues: {} };
        rollup[country].states[state].cities[city].total += count;

        const venueKey = `${venueId}__${venueName}`;
        if (!rollup[country].states[state].cities[city].venues[venueKey]) {
            rollup[country].states[state].cities[city].venues[venueKey] = { id: venueId, name: venueName, count: 0 };
        }
        rollup[country].states[state].cities[city].venues[venueKey].count += count;
    });

    const sortedCountries = Object.entries(rollup).sort((a, b) => b[1].total - a[1].total);

    // Flatten to rows for rendering
    const allRows = [];
    sortedCountries.forEach(([country, countryData]) => {
        const sortedStates = Object.entries(countryData.states).sort((a, b) => b[1].total - a[1].total);
        let countryFirstRow = true;
        sortedStates.forEach(([state, stateData]) => {
            const sortedCities = Object.entries(stateData.cities).sort((a, b) => b[1].total - a[1].total);
            let stateFirstRow = true;
            sortedCities.forEach(([city, cityData]) => {
                const sortedVenues = Object.values(cityData.venues).sort((a, b) => b.count - a.count);
                let cityFirstRow = true;
                sortedVenues.forEach(venue => {
                    allRows.push({
                        showCountry: countryFirstRow,
                        showState: stateFirstRow,
                        showCity: cityFirstRow,
                        country,
                        state,
                        city,
                        countryTotal: countryData.total,
                        stateTotal: stateData.total,
                        cityTotal: cityData.total,
                        venue,
                    });
                    countryFirstRow = false;
                    stateFirstRow = false;
                    cityFirstRow = false;
                });
            });
        });
    });

    return (
        <div style={styles.tableContainer}>
            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={styles.th}>Country</th>
                        <th style={styles.th}>State/Region</th>
                        <th style={styles.th}>City</th>
                        <th style={styles.th}>Venue</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Shows</th>
                    </tr>
                </thead>
                <tbody>
                    {allRows.map((row, idx) => (
                        <tr key={idx} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                            <td style={{ ...styles.td, fontWeight: row.showCountry ? 'bold' : 'normal', color: row.showCountry ? '#e0e0e0' : 'transparent' }}>
                                {row.showCountry
                                    ? <span>{row.country} <span style={styles.geoSubCount}>({row.countryTotal})</span></span>
                                    : '\u00a0'}
                            </td>
                            <td style={{ ...styles.td, paddingLeft: '24px', color: row.showState ? '#ccc' : 'transparent' }}>
                                {row.showState
                                    ? <span>{row.state} <span style={styles.geoSubCount}>({row.stateTotal})</span></span>
                                    : '\u00a0'}
                            </td>
                            <td style={{ ...styles.td, paddingLeft: '24px', color: row.showCity ? '#bbb' : 'transparent' }}>
                                {row.showCity
                                    ? <span>{row.city} <span style={styles.geoSubCount}>({row.cityTotal})</span></span>
                                    : '\u00a0'}
                            </td>
                            <td style={{ ...styles.td, paddingLeft: '32px' }}>
                                {row.venue.id
                                    ? <Link to={`/venues/${row.venue.slug || row.venue.id}`} style={styles.tableLink}>{row.venue.name}</Link>
                                    : <span style={{ color: '#aaa' }}>{row.venue.name}</span>}
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right' }}>
                                <span style={styles.showCount}>{row.venue.count}</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ArtistDetail({ slugOverride } = {}) {
    const params = useParams();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    // Attended state: map of event_id -> boolean
    const [attendedMap, setAttendedMap] = useState({});
    const [togglingMap, setTogglingMap] = useState({});

    const loadAttendedStatus = useCallback(async (shows) => {
        if (!shows || shows.length === 0) return;
        try {
            const { tokens } = await fetchAuthSession();
            const token = tokens.idToken.toString();
            const res = await fetch(`${config.API_URL}/user`, {
                headers: { Authorization: token }
            });
            if (!res.ok) return;
            // Fetch attended status for all event ids in one go via getUserEventStatus
            // We load each individually — batching can be added later
            const map = {};
            await Promise.all(shows.map(async (show) => {
                try {
                    const r = await fetch(`${config.API_URL}/user/event/${show.event_id}`, {
                        headers: { Authorization: token }
                    });
                    if (r.ok) {
                        const d = await r.json();
                        map[show.event_id] = d.attended;
                    }
                } catch (e) { /* skip */ }
            }));
            setAttendedMap(map);
        } catch (e) { /* not logged in or error */ }
    }, []);

    const handleToggleAttended = useCallback(async (eventId) => {
        setTogglingMap(prev => ({ ...prev, [eventId]: true }));
        try {
            const result = await toggleAttended(eventId);
            setAttendedMap(prev => ({ ...prev, [eventId]: result.attended }));
        } catch (e) {
            console.error('toggleAttended error:', e);
        } finally {
            setTogglingMap(prev => ({ ...prev, [eventId]: false }));
        }
    }, []);

    useEffect(() => {
        axios.get(`${config.API_URL}/artists/${slug}`)
            .then(res => {
                setData(res.data);
                setLoading(false);
                if (res.data && res.data.artist) {
                    document.title = `SHARC - ${res.data.artist.name}`;
                }
                if (res.data && res.data.show_history) {
                    loadAttendedStatus(res.data.show_history);
                }
            })
            .catch(() => {
                setError('Failed to load artist');
                setLoading(false);
            });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, [slug, loadAttendedStatus]);

    if (loading) return <div style={styles.message}>Loading artist...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const { artist, stats, played_with, top_venues, geography, show_history, festival_history, show_map, formerly_known_as, hometowns, links } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
        });
    };

    const headlinePercent = stats.total_shows > 0
        ? Math.round((stats.headline_shows / stats.total_shows) * 100)
        : 0;

    return (
        <div>
            {/* Nav row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Link to="/artists" style={styles.backLink}>← Back to Artists</Link>
                <Link to={`/artists/${artist.slug || artist.id}/edit`} style={styles.editButton}>Edit Artist</Link>
            </div>

            {/* Artist Header */}
            <div style={styles.header}>
                <div style={artist.image_url ? styles.headerWithImage : undefined}>
                    {artist.image_url && (
                        <img
                            src={artist.image_url}
                            alt={artist.name}
                            style={styles.artistImage}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    )}
                    <div style={styles.headerText}>
                        <h1 style={styles.title}>{artist.name}</h1>

                        {formerly_known_as && formerly_known_as.length > 0 && (
                            <div style={styles.fka}>
                                Formerly: {formerly_known_as.map(f => f.name).join(', ')}
                            </div>
                        )}

                        {[artist.city, artist.state, artist.country].filter(Boolean).length > 0 && (
                            <div style={styles.location}>
                                {[artist.city, artist.state, artist.country].filter(Boolean).join(', ')}
                            </div>
                        )}

                        {hometowns && hometowns.length > 0 && (
                            <div style={styles.fka}>
                                Hometown: {hometowns.map(ht => [ht.city, ht.state, ht.country].filter(Boolean).join(', ')).join(' · ')}
                            </div>
                        )}

                        <div style={styles.tags}>
                            {artist.artist_type && <span style={styles.tag}>{artist.artist_type}</span>}
                            {artist.artist_style && <span style={styles.tag}>{artist.artist_style}</span>}
                        </div>
                    </div>
                </div>

                {artist.description && (
                    <div style={styles.bio}>{artist.description}</div>
                )}
            </div>

            {/* Links */}
            {links && links.length > 0 && (
                <div style={styles.linksRow}>
                    {links.map(link => (
                        <a
                            key={link.id}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.linkChip}
                        >
                            {link.description} ↗
                        </a>
                    ))}
                </div>
            )}

            {/* Key Stats */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.total_shows || 0}</div>
                    <div style={styles.statLabel}>Total Shows</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.headline_shows || 0}</div>
                    <div style={styles.statLabel}>Headlining</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.support_shows || 0}</div>
                    <div style={styles.statLabel}>Support</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{headlinePercent}%</div>
                    <div style={styles.statLabel}>Headline Rate</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.avg_venue_capacity || '—'}</div>
                    <div style={styles.statLabel}>Avg Venue Size</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.biggest_venue_capacity || '—'}</div>
                    <div style={styles.statLabel}>Biggest Venue</div>
                </div>
            </div>

            {/* Show History quick facts */}
            <div style={styles.historyGrid}>
                <div style={styles.historyItem}>
                    <div style={styles.historyLabel}>First Show</div>
                    <div style={styles.historyValue}>{formatDate(stats.first_show_date)}</div>
                </div>
                <div style={styles.historyItem}>
                    <div style={styles.historyLabel}>Most Recent Show</div>
                    <div style={styles.historyValue}>{formatDate(stats.last_show_date)}</div>
                </div>
                {stats.first_show_date && stats.last_show_date && (() => {
                    let d1 = new Date(stats.first_show_date);
                    let d2 = new Date(stats.last_show_date);
                    let years = d2.getUTCFullYear() - d1.getUTCFullYear();
                    let months = d2.getUTCMonth() - d1.getUTCMonth();
                    let days = d2.getUTCDate() - d1.getUTCDate();
                    if (days < 0) {
                        months -= 1;
                        days += new Date(d2.getUTCFullYear(), d2.getUTCMonth(), 0).getDate();
                    }
                    if (months < 0) { years -= 1; months += 12; }
                    const parts = [];
                    if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
                    if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
                    if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
                    return (
                        <div style={styles.historyItem}>
                            <div style={styles.historyLabel}>Term of Shows</div>
                            <div style={styles.historyValue}>{parts.join(' ') || '—'}</div>
                        </div>
                    );
                })()}
            </div>

            {/* Tabbed Section */}
            <div style={styles.tabsWrapper}>
                <div style={styles.tabBar}>
                    {TABS.map((tab, i) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(i)}
                            style={{
                                ...styles.tabBtn,
                                ...(activeTab === i ? styles.tabBtnActive : {}),
                            }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
                <div style={styles.tabContent}>
                    {activeTab === 0 && <ShowHistoryTab shows={show_history} attendedMap={attendedMap} onToggleAttended={handleToggleAttended} togglingMap={togglingMap} />}
                    {activeTab === 1 && <ArtistMembersTab artistId={artist.id} />}
                    {activeTab === 2 && <ShowsByYearTab shows={show_history} />}
                    {activeTab === 3 && <FestivalsPlayedTab festivals={festival_history} shows={show_history} />}
                    {activeTab === 4 && <VenuesPlayedTab venues={top_venues} />}
                    {activeTab === 5 && <ArtistRelationshipsTab playedWith={played_with} />}
                    {activeTab === 6 && <GeographicBreakdownTab geography={geography} />}
                    {activeTab === 7 && <GeographicMapTab showMap={show_map} />}
                </div>
            </div>
        </div>
    );
}

const styles = {
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px' },
    editButton: { backgroundColor: '#2c2c2c', color: '#bb86fc', border: '1px solid #bb86fc', borderRadius: '4px', padding: '6px 16px', fontSize: '14px', textDecoration: 'none', fontWeight: 'bold' },
    header: { marginBottom: '24px' },
    headerWithImage: { display: 'flex', gap: '24px', alignItems: 'flex-start', marginBottom: '16px' },
    headerText: { flex: 1 },
    artistImage: { width: '140px', height: '140px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', flexShrink: 0 },
    title: { fontSize: '40px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '6px' },
    fka: { fontSize: '14px', color: '#888', fontStyle: 'italic', marginBottom: '6px' },
    location: { fontSize: '16px', color: '#aaa', marginBottom: '8px' },
    tags: { display: 'flex', gap: '8px', marginBottom: '8px' },
    tag: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '4px 10px', fontSize: '13px', color: '#bb86fc' },
    bio: { fontSize: '15px', color: '#ccc', lineHeight: '1.7', marginTop: '16px', padding: '16px', backgroundColor: '#1e1e1e', borderRadius: '8px', border: '1px solid #2a2a2a' },
    linksRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '32px' },
    linkChip: { backgroundColor: '#1e1e1e', border: '1px solid #444', borderRadius: '4px', padding: '6px 14px', fontSize: '13px', color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px', textAlign: 'center' },
    statValue: { fontSize: '36px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },
    historyGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' },
    historyItem: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px' },
    historyLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
    historyValue: { fontSize: '18px', fontWeight: 'bold', color: '#e0e0e0' },
    tabsWrapper: { marginTop: '8px' },
    tabBar: { display: 'flex', borderBottom: '2px solid #333', marginBottom: '0', overflowX: 'auto', gap: '2px' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-2px', padding: '12px 20px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px' },
    tabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '200px' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '11px 16px', color: '#e0e0e0', borderBottom: '1px solid #222' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    showCount: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    attendedBtnOff: { backgroundColor: 'transparent', color: '#666', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    geoSubCount: { fontSize: '12px', color: '#666', fontWeight: 'normal' },
    pagination: { display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '20px', flexWrap: 'wrap' },
    pageBtn: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 12px', fontSize: '13px', color: '#aaa', cursor: 'pointer' },
    pageBtnActive: { backgroundColor: '#bb86fc', borderColor: '#bb86fc', color: '#121212', fontWeight: 'bold' },
    pageBtnDisabled: { opacity: 0.35, cursor: 'default' },
    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '60px 0', fontSize: '15px' },
    emptyIcon: { fontSize: '32px', marginBottom: '12px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
};

export default ArtistDetail;